import { mediaKey, type MediaKey } from '@/store/slices/mediaSlice';
import type { WatchEntry } from '@/services/historyApi';
import type { MediaDetails } from '@/types/streaming';
import type { HydrationState } from '@/hooks/useHydratedMedia';

export interface EligibilityThresholds {
  /** Drops entries with less than this many ms of progress. */
  resumeMinProgressMs: number;
  /** Percent (0-100). Drops entries whose progress / runtime exceeds this. */
  nearCompleteThreshold: number;
}

/**
 * Compute the runtime in milliseconds for the title associated with a watch
 * entry, given the hydrated TMDB metadata. Returns `null` when the runtime
 * isn't knowable from this metadata (e.g. TV details don't expose per-episode
 * runtime today). Callers treat `null` as "filter doesn't apply yet" so the
 * card stays visible.
 */
export function runtimeMsFor(entry: WatchEntry, details: MediaDetails): number | null {
  if (details.type === 'movie' && entry.movieId !== null) {
    if (typeof details.runtimeMinutes === 'number' && details.runtimeMinutes > 0) {
      return details.runtimeMinutes * 60_000;
    }
    return null;
  }
  // No per-episode runtime is exposed on TvDetails — leave near-complete
  // filtering off for TV entries until that becomes available.
  return null;
}

/** Key the entry will be hydrated under. Returns null for malformed entries. */
export function entryHydrationKey(entry: WatchEntry): MediaKey | null {
  if (entry.movieId !== null) return mediaKey('movie', entry.movieId);
  if (entry.tvId !== null) return mediaKey('tv', entry.tvId);
  return null;
}

/**
 * Apply the Continue Watching shelf's two filter rules:
 *  1. progress must be >= `resumeMinProgressMs` (drops accidental taps).
 *  2. once runtime is known and positive, progress / runtime must be
 *     <= `nearCompleteThreshold / 100` (drops finished entries).
 *
 * While hydration is still pending (or absent) the entry is kept — we'd
 * rather show a card that's about to disappear than hide a whole shelf
 * during a slow TMDB response. If hydration outright errors, the entry is
 * dropped (the card has no metadata to render anyway).
 */
export function filterEligibleEntries(
  entries: WatchEntry[],
  hydrated: Map<MediaKey, HydrationState>,
  thresholds: EligibilityThresholds,
): WatchEntry[] {
  const fraction = thresholds.nearCompleteThreshold / 100;
  const out: WatchEntry[] = [];
  for (const entry of entries) {
    if (entry.progress < thresholds.resumeMinProgressMs) continue;

    const key = entryHydrationKey(entry);
    if (key === null) continue;

    const hydration = hydrated.get(key);
    if (hydration?.status === 'error') continue;

    if (hydration?.status === 'ready' && hydration.data) {
      const runtimeMs = runtimeMsFor(entry, hydration.data);
      if (runtimeMs !== null && entry.progress / runtimeMs > fraction) continue;
    }

    out.push(entry);
  }
  return out;
}
